import { axioTokens } from "../theme";
import { RELATIVE_DAY_MS, RELATIVE_HOUR_MS, RELATIVE_MINUTE_MS, RELATIVE_WEEK_DAYS } from "../constants/matching";

export function formatPaise(paise: number | null | undefined, currency?: string): string {
  if (paise === null || paise === undefined) return "—";
  const rupees = paise / 100;
  const amount =
    rupees >= 100000 ? `₹${(rupees / 100000).toFixed(2)} L` : `₹${rupees.toLocaleString("en-IN")}`;
  return currency ? `${amount} ${currency}` : amount;
}

/** Rate-card commercial terms label for tables and cards. */
export function contractTermsLabel(row: {
  fee_basis: string;
  fee_flat_paise?: number | null;
  fee_pct_ctc?: number | null;
}): string {
  return row.fee_basis === "flat"
    ? `Flat ${formatPaise(row.fee_flat_paise ?? 0)}`
    : `${row.fee_pct_ctc ?? 0}% CTC`;
}

/** Compact multi-city label: Bengaluru or Bengaluru +2 */
export function formatCities(cities: string[] | null | undefined): string {
  const list = (cities ?? []).map((c) => c.trim()).filter(Boolean);
  if (list.length === 0) return "—";
  if (list.length === 1) return list[0];
  return `${list[0]} +${list.length - 1}`;
}

/** Estimate placement fee in paise (flat or % of CTC band). */
export function estimateFeePaise(job: {
  fee_basis: string;
  fee_flat_paise: number | null;
  fee_pct_ctc: number | null;
  ctc_min_paise: number | null;
  ctc_max_paise: number | null;
}): { min: number; max: number } | null {
  if (job.fee_basis === "flat") {
    const flat = job.fee_flat_paise ?? 0;
    if (!flat) return null;
    return { min: flat, max: flat };
  }
  const pct = job.fee_pct_ctc ?? 0;
  if (!pct) return null;
  const ctcMin = job.ctc_min_paise ?? job.ctc_max_paise ?? 0;
  const ctcMax = job.ctc_max_paise ?? job.ctc_min_paise ?? 0;
  if (!ctcMin && !ctcMax) return null;
  return {
    min: Math.floor((ctcMin * pct) / 100),
    max: Math.floor((ctcMax * pct) / 100),
  };
}

/** Affiliate/recruiter slice of the placement fee. */
export function estimateSharePaise(
  fee: { min: number; max: number } | null,
  sharePct: number | null | undefined,
): { min: number; max: number } | null {
  if (!fee || sharePct == null) return null;
  return {
    min: Math.floor((fee.min * sharePct) / 100),
    max: Math.floor((fee.max * sharePct) / 100),
  };
}

export function formatPaiseRange(min: number, max: number): string {
  if (min === max) return formatPaise(min);
  return `${formatPaise(min)} – ${formatPaise(max)}`;
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

export function formatRelativeTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const elapsed = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(elapsed)) return "—";
  if (elapsed < RELATIVE_MINUTE_MS) return "just now";
  if (elapsed < RELATIVE_HOUR_MS) return `${Math.floor(elapsed / RELATIVE_MINUTE_MS)}m ago`;
  if (elapsed < RELATIVE_DAY_MS) return `${Math.floor(elapsed / RELATIVE_HOUR_MS)}h ago`;
  const days = Math.floor(elapsed / RELATIVE_DAY_MS);
  if (days < RELATIVE_WEEK_DAYS) return `${days}d ago`;
  return formatDate(iso);
}

function addCalendarDays(iso: string, days: number): Date {
  const [year, month, day] = iso.slice(0, 10).split("-").map(Number);
  const d = new Date(Date.UTC(year, month - 1, day));
  d.setUTCDate(d.getUTCDate() + days);
  return d;
}

/** Display-only payout window: 10–30 days after the guarantee end date. */
export function formatExpectedPayoutWindow(guaranteeUntil: string | null | undefined): string {
  if (!guaranteeUntil) return "—";
  const start = addCalendarDays(guaranteeUntil, 10);
  const end = addCalendarDays(guaranteeUntil, 30);
  const fmt = (d: Date) =>
    d.toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
      timeZone: "UTC",
    });
  return `${fmt(start)} – ${fmt(end)}`;
}

export function splitExperience(years: number | null | undefined): { years: number; months: number } {
  if (years == null || Number.isNaN(Number(years))) return { years: 0, months: 0 };
  const total = Number(years);
  const y = Math.floor(total + 1e-9);
  let m = Math.round((total - y) * 12);
  if (m === 12) return { years: y + 1, months: 0 };
  if (m < 0) return { years: y, months: 0 };
  return { years: y, months: m };
}

export function combineExperience(years?: number | null, months?: number | null): number | undefined {
  if (years == null && months == null) return undefined;
  return Math.round(((years ?? 0) + (months ?? 0) / 12) * 10) / 10;
}

export function formatExperience(years: number | null | undefined): string {
  if (years == null) return "—";
  const parts = splitExperience(years);
  const bits: string[] = [];
  if (parts.years > 0) bits.push(`${parts.years} yr${parts.years === 1 ? "" : "s"}`);
  if (parts.months > 0) bits.push(`${parts.months} mo`);
  return bits.length ? bits.join(" ") : "0 yrs";
}

/** Job listing/detail experience band from min/max years. */
export function formatJobExperienceRange(
  min: number | null | undefined,
  max: number | null | undefined,
): string | null {
  if (min == null && max == null) return null;
  if (min != null && max != null) return `${min}–${max} yrs`;
  if (min != null) return `${min}+ yrs`;
  return `Up to ${max} yrs`;
}

export function formatAvailabilityDays(n: number | null | undefined): string {
  if (n == null) return "—";
  if (n === 0) return "Immediate";
  return `${n} day${n === 1 ? "" : "s"}`;
}

export const CANDIDATE_SOURCE_LABELS: Record<string, string> = {
  affiliate_link: "Affiliate link",
  recruiter_link: "Recruiter link",
  campaign_inbound: "Campaign",
  recruiter_pool: "Recruiter pool",
  bulk_import: "Bulk import",
};

export function candidateSourceLabel(source: string | null | undefined): string {
  if (!source) return "—";
  return CANDIDATE_SOURCE_LABELS[source] ?? source.replaceAll("_", " ");
}

export { SEGMENT_LABELS, SEGMENT_OPTIONS, segmentLabel } from "./segments";

export const STAGE_TYPE_LABELS: Record<string, string> = {
  screening: "Screening",
  assessment: "Assessment",
  interview: "Interview",
  ops: "Ops",
  custom: "Custom",
};

/** Mantine color name — kept for Badge consumers that still need a named color. */
export function statusColor(status: string): string {
  switch (status) {
    case "hired":
    case "verified":
    case "open":
    case "active":
      return "green";
    case "rejected":
    case "closed":
    case "suspended":
      return "red";
    case "in_progress":
    case "in_pipeline":
      return "yellow";
    case "in_pool":
      return "gray";
    case "pending_hr_review":
    case "pending_review":
    case "pending":
      return "orange";
    case "paused":
    case "filled":
    case "submitted":
    case "issued":
      return "blue";
    case "paid":
      return "green";
    case "partial":
      return "orange";
    case "overdue":
      return "red";
    case "cancelled":
    case "withdrawn":
      return "gray";
    default:
      return "gray";
  }
}

/** Hex dots for the operations-board StatusBadge — desaturated, consistent chroma. */
export function statusDotColor(status: string): string {
  switch (status) {
    case "hired":
    case "verified":
    case "open":
    case "active":
    case "released":
      return axioTokens.money;
    case "rejected":
    case "closed":
    case "suspended":
    case "failed":
      return axioTokens.danger;
    case "in_progress":
    case "in_pipeline":
    case "investigating":
      return axioTokens.warningDarker;
    case "pending_hr_review":
    case "pending_review":
    case "pending":
    case "scheduled":
    case "on_hold":
      return axioTokens.warningDark;
    case "paused":
    case "filled":
    case "draft":
    case "submitted":
    case "issued":
      return axioTokens.roleBlue;
    case "paid":
      return axioTokens.money;
    case "partial":
      return axioTokens.warningDark;
    case "overdue":
      return axioTokens.danger;
    case "in_pool":
    case "withdrawn":
    case "superseded":
    case "archived":
    case "cancelled":
    default:
      return axioTokens.neutral400;
  }
}

export const STATUS_LABELS: Record<string, string> = {
  in_progress: "In Progress",
  pending_hr_review: "Pending HR Review",
  pending_review: "Pending Review",
  draft: "Draft",
  open: "Open",
  paused: "Paused",
  filled: "Filled",
  closed: "Closed",
  hired: "Hired",
  rejected: "Rejected",
  withdrawn: "Withdrawn",
  in_pool: "In pool",
  in_pipeline: "In pipeline",
  active: "Active",
  superseded: "Superseded",
  archived: "Archived",
  verified: "Verified",
  submitted: "Submitted",
  issued: "Issued",
  partial: "Partial",
  paid: "Paid",
  overdue: "Overdue",
  cancelled: "Cancelled",
};

export const INVOICE_STATUS_LABELS: Record<string, string> = {
  draft: "Draft",
  issued: "Issued",
  partial: "Partial",
  paid: "Paid",
  overdue: "Overdue",
  cancelled: "Cancelled",
};

export function invoiceStatusColor(status: string): string {
  switch (status) {
    case "draft":
      return "gray";
    case "issued":
      return "blue";
    case "partial":
      return "orange";
    case "paid":
      return "green";
    case "overdue":
      return "red";
    case "cancelled":
    default:
      return "gray";
  }
}

export const OUTCOME_LABELS: Record<string, string> = {
  passed: "Passed",
  failed: "Failed",
  skipped: "Skipped",
  moved_to: "Moved to stage",
  submitted_for_review: "Submitted for HR review",
};

export const ACTOR_LABEL_RECRUITER = "Recruiter";
export const ACTOR_LABEL_HIRING_TEAM = "Hiring team";
export const STAGE_HISTORY_TITLE = "Stage History";
export const REJECTION_BANNER_TITLE = "Rejected";
export const HIRE_FEEDBACK_BANNER_TITLE = "Hiring team notes";
export const RECRUITER_PITCH_LABEL = "Recruiter pitch";
export const YOUR_PITCH_LABEL = "Your pitch";
export const RECRUITER_SUBMISSION_SUBTITLE = "Recruiter submission";

const OUTCOME_PASSED = "passed";
const OUTCOME_SUBMITTED_FOR_REVIEW = "submitted_for_review";

export function hireFeedbackNotes(
  history: Array<{ outcome: string; notes: string | null }>,
): string | null {
  const reviewIndex = history.findLastIndex((event) => event.outcome === OUTCOME_SUBMITTED_FOR_REVIEW);
  const afterReview = reviewIndex >= 0 ? history.slice(reviewIndex + 1) : history;
  for (let index = afterReview.length - 1; index >= 0; index -= 1) {
    const event = afterReview[index];
    const notes = event.notes?.trim();
    if (event.outcome === OUTCOME_PASSED && notes) {
      return notes;
    }
  }
  return null;
}

export const PAYOUT_STATUS_LABELS: Record<string, string> = {
  scheduled: "Scheduled",
  on_hold: "On Hold",
  released: "Released",
  paid: "Paid",
  failed: "Failed",
};

export const DISPUTE_TYPE_LABELS: Record<string, string> = {
  duplicate_claim: "Duplicate claim",
  attribution: "Attribution",
  no_join: "No join",
  fee: "Fee",
  quality: "Quality",
  payment: "Payment",
  other: "Other",
};

export const DISPUTE_STATUS_LABELS: Record<string, string> = {
  open: "Open",
  investigating: "Investigating",
  resolved: "Resolved",
  rejected: "Rejected",
};

export function disputeStatusColor(status: string): string {
  switch (status) {
    case "open":
      return "orange";
    case "investigating":
      return "blue";
    case "resolved":
      return "green";
    case "rejected":
      return "gray";
    default:
      return "gray";
  }
}

export function payoutStatusColor(status: string): string {
  switch (status) {
    case "paid":
      return "green";
    case "scheduled":
      return "blue";
    case "released":
      return "teal";
    case "on_hold":
      return "orange";
    case "failed":
      return "red";
    default:
      return "gray";
  }
}
